/**
 * ChangeHistorySection — 모듈 상세 '변경 이력' 섹션 (queue #672 §UAT-2026-07-09-R672
 * §R672-4 · §R665 `ConfigSection`·§R666 `TroubleshootingSection`·§R667 `AlternativesLink` 계보).
 *
 * `ModuleDetailBody` 에 삽입되는 노드 상세 공유 섹션. 데이터는 §R643 이 이미 커밋한
 * `data/module-maturity.json` 을 `selectModuleHistory(maturity, node.artifact, new Date())`
 * 로 조회한다(BE 0 · 정적 import · `MaturityBadge.tsx`/`TakeThisPanel.tsx` 와 동일 SSOT 재사용).
 * 0건(해당 artifact 이력 부재·`gitAvailable:false`·`commits` 0건)이면 섹션 전체를 숨긴다
 * (빈 표/EmptyState 미노출 — §R665/§R666 설계 결정 답습).
 *
 * ★딥링크 미생성 — §R647 `consumerGuideLink.ts`(GitLab blob 직링크 제거: 혼합 콘텐츠·SSO 벽·
 * env 미주입 3종 결함) + #652 `VersionHistoryPanel.tsx`(짧은 SHA 평문 표기 통일) 선례에 따라
 * 커밋 행은 하이퍼링크를 생성하지 않는다 — 짧은 SHA 는 `<code>` 평문(사용자가 `git show`/
 * GitLab 검색에 수동 사용).
 */
import { useTranslation } from 'react-i18next';
import type { ModuleNode, ModuleMaturity } from '../types/showcase';
import { selectModuleHistory } from '../data/recent-changes';
import moduleMaturityJson from '../data/module-maturity.json';

const MODULE_MATURITY = moduleMaturityJson as unknown as ModuleMaturity;

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-lg)',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  margin: '0 0 var(--spacing-1)',
};

const captionStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  margin: '0 0 var(--spacing-3)',
};

const headerCellStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderBottom: '1px solid var(--color-surface-border)',
  color: 'var(--color-text-muted)',
  fontWeight: 600,
  fontSize: 'var(--font-size-xs)',
};

const dateCellStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderBottom: '1px solid var(--color-surface-border)',
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  whiteSpace: 'nowrap',
  verticalAlign: 'top',
};

const subjectCellStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderBottom: '1px solid var(--color-surface-border)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: 0,
  verticalAlign: 'top',
};

const shaCellStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderBottom: '1px solid var(--color-surface-border)',
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  whiteSpace: 'nowrap',
  verticalAlign: 'top',
};

interface Props {
  node: ModuleNode;
  /** 테스트용 fixture 오버라이드 — 기본은 정적 import `MODULE_MATURITY`(MaturityBadge/
   * TakeThisPanel/ModuleDocsModal 과 동일 SSOT). 런타임 호출부는 지정하지 않는다. */
  maturity?: ModuleMaturity;
}

export function ChangeHistorySection({ node, maturity = MODULE_MATURITY }: Props) {
  const { t } = useTranslation();
  const history = selectModuleHistory(maturity, node.artifact, new Date());

  // §R672-4 empty — 대상 artifact 이력 부재/`gitAvailable:false`/0건 → 섹션 전체 숨김.
  if (!history) return null;

  const captionKey = maturity.shallow ? 'showcase.changeHistory.captionShallow' : 'showcase.changeHistory.caption';

  return (
    <section aria-labelledby="change-history-section-title" style={{ marginBottom: 'var(--spacing-4)' }}>
      <h3 id="change-history-section-title" style={sectionTitleStyle}>
        {t('showcase.changeHistory.title')}
      </h3>
      <p style={captionStyle}>
        {t(captionKey, { date: history.lastChanged ?? '—', count: history.commitCount })}
      </p>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={headerCellStyle}>{t('showcase.changeHistory.col.date')}</th>
            <th style={headerCellStyle}>{t('showcase.changeHistory.col.subject')}</th>
            <th style={headerCellStyle} title={t('showcase.changeHistory.shaTip')}>
              {t('showcase.changeHistory.col.sha')}
            </th>
          </tr>
        </thead>
        <tbody>
          {history.commits.map((commit, idx) => (
            <tr key={`${commit.hash ?? ''}-${commit.date}-${idx}`}>
              <td style={dateCellStyle}>
                <code>{commit.date}</code>
              </td>
              <td style={subjectCellStyle} title={commit.subject}>
                {commit.subject}
              </td>
              <td style={shaCellStyle}>
                {commit.hash ? <code title={t('showcase.changeHistory.shaTip')}>{commit.hash.slice(0, 8)}</code> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
